import Vue from "vue";
import Router from "vue-router";
import {ActiveUser} from "@/services/user";
import {IS_MZ_BRANDING} from "@/constants/config";
import {userRoles} from "../constants/userRoles";

// Containers
const TheContainer = () => import("@/containers/TheContainer.vue");

//Home
const Home = () => import("@/views/Home.vue");

//General Data
const FileList = () => import("@/views/general_data/FileList.vue");
const GeneralReports = () => import("@/views/general_data/Reports.vue");

//Campaigns
const NewCampaign = () => import("@/views/campaigns/NewCampaign.vue");
const CampaignsView = () => import("@/views/campaigns/CampaignsView.vue");
const CampaignDataAnalysis = () => import("@/views/campaigns/CampaignDataAnalysis.vue");
const CampaignUploadView = () => import("@/views/campaigns/CampaignUploadView.vue");
const CampaignDataDetailsView = () => import("@/views/campaigns/CampaignDataDetailsView.vue");

const CampaignTaux = () => import('@/views/campaigns/CampaignTaux.vue')

// Views - Pages
const Page404 = () => import("@/views/pages/Page404.vue");
const Page500 = () => import("@/views/pages/Page500.vue");
const Login = () => import("@/views/pages/Login.vue");
const ForgotPassword = () => import("@/views/pages/ForgotPassword.vue");
const ResetPassword = () => import("@/views/pages/ResetPassword.vue");
const Register = () => import("@/views/pages/Register.vue");

// Members
const Members = () => import("@/views/members/MembersView.vue");
const Member = () => import("@/views/members/MemberView.vue");
const MemberMapping = () => import("@/views/members/MemberMappingView.vue");
// Users
const Users = () => import("@/views/users/UsersView.vue");
const User = () => import("@/views/users/UserView.vue");
//Branches
const Branch = () => import("@/views/branches/BranchesView.vue");
//Files
const Files = () => import("@/views/files/FilesView.vue");
//Annexes
const AnnexeIView = () => import("@/views/annexes/AnnexeIView.vue");
const AnnexeIIView = () => import("@/views/annexes/AnnexeIIView.vue");
const AnnexeIIIView = () => import("@/views/annexes/AnnexeIIIView.vue");

//Analysis
const AnalysisView = () => import("@/views/analysis/AnalysisView.vue")

// Reports
const Reports = () => import("@/views/Reports/Reports.vue")

// Historical
const Historical = () => import("@/views/historical/Historical.vue")

//Cover Year
const NewCoverYear = () => import("@/views/coveryears/NewCoverYear.vue");
const CoverYearsView = () => import("@/views/coveryears/CoverYearsView.vue")
const CoverYearTaux = () => import('@/views/coveryears/CoverYearTaux.vue')
const CoverYearCCR = () => import('@/views/coveryears/CoverYearCCR.vue')
const CoverYearCCRThreshold = () => import('@/views/coveryears/CoverYearCCRThreshold.vue')
const DeclaringMembers = () => import('@/views/coveryears/DeclaringMembers.vue')
const CoverYearCCRGroup = () => import('@/views/coveryears/CoverYearCCRGroup.vue')

Vue.use(Router);

const router = new Router({
  mode: "history",
  linkActiveClass: "active",
  scrollBehavior: () => ({y: 0}),
  routes: configRoutes(),
});

const isOpenAccess = (route) =>
  route.matched.some((route) => route.meta?.isOpenAccess);

const hasAccess = (route) => {
  const requiredRole = route.meta?.requiredRole || route.matched[1]?.meta?.requiredRole;
  if (!requiredRole) return true;
  return ActiveUser.hasRole(requiredRole)
};

const isFound = (route) => route.matched[0].name !== "NotFound";

router.beforeEach((to, from, next) => {
  if (to.meta?.getTitle) to.meta.title = to.meta.getTitle(to);
  const isAuthenticated = ActiveUser.get();
  if (!isAuthenticated && !isOpenAccess(to)) {
    if (isFound(to)) {
      localStorage.setItem("pathToLoadAfterLogin", to.path);
    }
    return next({name: IS_MZ_BRANDING ? "Login" : "ManagementLogin"});
  }

  if (!hasAccess(to)) return next({name: "Home"});

  return next();
});
export default router;

function configRoutes() {
  return [
    {
      path: "/",
      component: TheContainer,
      children: [
        {
          path: "",
          meta: {
            label: "Home",
          },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "/",
              name: "Home",
              component: Home,
            },
            {
              path: "campaign",
              meta: {
                requiredRole: userRoles.User,
                label: "Campaigns",
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  name: "Campaigns",
                  path: "",
                  component: CampaignsView,
                },
                {
                  path: "new",
                  name: "New Campaign",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: NewCampaign,
                },
                {
                  path: ":campaignId",
                  name: "Campaign Details",
                  component: {
                    render(c) {
                      return c("router-view");
                    },
                  },
                  meta: {
                    paramKey: "campaignId",
                  },
                  children: [
                    {
                      path: "",
                      name: "Campaign Details",
                      meta: {
                        label: "Campaign",
                        requiredRole: userRoles.Admin
                      },
                      component: CampaignTaux,
                    },
                    {
                      path: "upload",
                      name: "CampaignUpload",
                      meta: {
                        requiredRole: userRoles.User,
                        label: "File upload",
                      },
                      component: CampaignUploadView,
                    },
                    {
                      redirect: "upload",
                      path: "member",
                      component: {
                        render(c) {
                          return c("router-view");
                        },
                      },
                      children: [
                        {
                          path: "",
                          meta: {
                            label: "Members",
                          },
                          component: Members,
                        },
                        {
                          path: ":id",
                          component: {
                            render(c) {
                              return c("router-view");
                            },
                          },
                          children: [
                            {
                              path: "",
                              name: "Member Details",
                              meta: {
                                paramKey: "id",
                                label: "Member Details",
                              },
                              component: Member,
                            },
                            {
                              path: 'quality',
                              name: 'Data quality',
                              meta: {
                                label: "Data quality"
                              },
                              component: CampaignDataAnalysis,
                            },
                            {
                              path: 'mapping',
                              name: "Insurance branch codification",
                              component: MemberMapping,
                              meta: {
                                label: "Insurance branch codification"
                              }
                            },
                            {
                              path: 'riskfile',
                              name: 'Risks at the address file',
                              component: CampaignDataDetailsView,
                              meta: {
                                label: "Risks at the address file"
                              }
                            },
                            {
                              path: 'annexi',
                              name: "Appendix I",
                              component: AnnexeIView,
                              meta: {
                                label: "Appendix I"
                              }
                            },
                            {
                              path: 'annexii',
                              name: "Appendix II",
                              component: AnnexeIIView,
                              meta: {
                                label: "Appendix II"
                              },
                            },
                            {
                              path: 'annexiii',
                              name: "Appendix III",
                              component: AnnexeIIIView,
                              meta: {
                                label: "Appendix III"
                              }
                            },
                            {
                              path: 'analysis',
                              name: "Data Analysis",
                              component: AnalysisView,
                            },
                          ]
                        },
                      ],
                    },
                    {
                      path: 'reports',
                      name: "Reports",
                      meta: {
                        requiredRole: userRoles.Admin
                      },
                      component: Reports
                    },
                  ],
                },
              ],
            },
            {
              path: "admin",
              redirect: "/admin/users",
              name: "Admin",
              meta: {
                requiredRole: userRoles.Admin
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "users",
                  meta: {
                    label: "Users",
                  },
                  component: {
                    render(c) {
                      return c("router-view");
                    },
                  },
                  children: [
                    {
                      path: "",
                      name: "Users",
                      component: Users,
                    },
                    {
                      path: ":id",
                      meta: {
                        label: "User Details",
                      },
                      name: "User",
                      component: User,
                    },
                  ],
                },
                {
                  path: "members",
                  meta: {
                    label: "Members",
                  },
                  component: {
                    render(c) {
                      return c("router-view");
                    },
                  },
                  children: [
                    {
                      path: "",
                      name: "Members",
                      component: Members,
                    },
                    {
                      path: ":id",
                      meta: {
                        label: "Member Details",
                      },
                      name: "Member",
                      component: Member,
                    },
                  ],
                },
                {
                  path: "branch",
                  meta: {
                    label: "Branch",
                  },
                  component: Branch
                },
                {
                  path: "files",
                  meta: {
                    label: "Files",
                  },
                  component: Files
                },
              ],
            },
            {
              path: "general_data",
              name: "General data",
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  name: "Results",
                  path: "results",
                  meta: {
                    requiredRole: userRoles.SuperUser
                  },
                  component: FileList,
                },
                {
                  name: "Reports",
                  path: "reports",
                  meta: {
                    requiredRole: userRoles.SuperUser
                  },
                  component: GeneralReports,
                },
              
              ]
            },
            // {
            //   name: "Historical",
            //   path: "historical",
            //   meta: {
            //     requiredRole: userRoles.Admin
            //   },
            //   component: Historical
            // },
            {
              path: "coveryear",
              meta: {
                requiredRole: userRoles.User,
                label: "Cover Year",
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  name: "Cover Year",
                  path: "",
                  component: CoverYearsView,
                },
                {
                  path: "new",
                  name: "New Cover Year",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: NewCoverYear,
                },
                {
                  path: "taux",
                  name: "GAREAT Rates",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: CoverYearTaux,
                },

                {
                  path: "declaring-members",
                  name: "Declaring Members",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: DeclaringMembers,
                },
                {
                  path: "CCR",
                  name: "CCR Rates",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: CoverYearCCR,
                },
                {
                  path: "CCR_threshold",
                  name: "CCR Threshold Rates",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: CoverYearCCRThreshold,
                },
                {
                  path: "CCR_group",
                  name: "CCR Group",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: CoverYearCCRGroup,
                },
                {
                  path: "declaring-members",
                  name: "Declaring Members",
                  meta: {
                    requiredRole: userRoles.Admin
                  },
                  component: DeclaringMembers,
                },
              ],
            },
          ],
        },
        {
          path: "users/me",
          name: "My Profile",
          component: User,
        },
      ],
    },
    {
      path: "/oauth",
      name: "OAuth Login",
      meta: {
        isOpenAccess: true,
      },
      component: Login,
    },
    {
      path: "/account",
      name: "account",
      meta: {
        isOpenAccess: true,
      },
      component: {
        render(c) {
          return c("router-view");
        },
      },
      children: [
        {
          path: "login",
          name: "Login",
          component: Login,
        },
        {
          path: "forgot",
          name: "Forgot password",
          component: ForgotPassword,
        },
        {
          path: "reset/:token",
          name: "Password reset",
          component: ResetPassword,
        },
        {
          path: "login_management",
          name: "ManagementLogin",
          component: Login,
          props: {
            show_email_login: true,
          },
        },
      ],
    },
    {
      path: "/pages",
      redirect: "/pages/404",
      name: "Pages",
      component: {
        render(c) {
          return c("router-view");
        },
      },
      children: [
        {
          path: "404",
          name: "Page404",
          component: Page404,
        },
        {
          path: "500",
          name: "Page500",
          component: Page500,
        },
        {
          path: "register",
          name: "Register",
          component: Register,
        },
      ],
    },
    {
      path: "*",
      name: "NotFound",
      component: TheContainer,
      children: [{path: "*", component: Page404}],
    },
  ];
}
